// src/pages/LandingPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import * as d1Api from '../services/d1Api';
import { 
  CheckCircle, 
  Users, 
  Video, 
  Headphones,
  BookOpen,
  Target,
  Clock,
  LogIn,
  UserPlus,
  ArrowLeft,
  X,
  Award,
  Youtube,
  Facebook,
  MessageCircle,
  Instagram,
  Sun,
  Moon
} from 'lucide-react';

export const LandingPage = () => {

  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [topStudents, setTopStudents] = useState([]);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showHelpMessage, setShowHelpMessage] = useState(false);
  const [showHelpMenu, setShowHelpMenu] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isDark, setIsDark] = useState(true);
  const { user } = useAuth();

  // Dummy data for honored students (temporary until profiles endpoint is ready)
  const honoredStudents = [];

  useEffect(() => {
    // Scroll progress listener
    const handleScroll = () => {
      const totalScroll = document.documentElement.scrollHeight - window.innerHeight;
      const currentProgress = (window.scrollY / totalScroll) * 100;
      setScrollProgress(currentProgress);
    };
    window.addEventListener('scroll', handleScroll);

    // Load courses from D1
    const fetchCourses = async () => {
      try {
        const response = await d1Api.getSuggestedCourses();
        if (response.success && response.data) {
          setCourses(response.data);
        } else {
          // Fallback to dummy courses if API fails
          setCourses([
            {
              id: 1,
              title: 'النحو التطبيقي - أولى ثانوي',
              description: 'شرح كامل لقواعد النحو مع تطبيقات عملية',
              price: 299,
              duration_days: 60,
              grade: '1sec'
            },
            {
              id: 2,
              title: 'البلاغة والأدب - تانية ثانوي',
              description: 'فنون البلاغة والأدب العربي بأسلوب مبسط',
              price: 399,
              duration_days: 90,
              grade: '2sec'
            },
            {
              id: 3,
              title: 'النحو المتقدم - تالتة ثانوي',
              description: 'مراجعة شاملة مع حل امتحانات السنوات السابقة',
              price: 499,
              duration_days: 120,
              grade: '3sec'
            }
          ]);
        }
      } catch (err) {
        console.error('Error fetching courses:', err);
        // Set dummy courses on error
        setCourses([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCourses();

    // Load leaderboard from API
    const fetchLeaderboard = async () => {
      try {
        const res = await d1Api.getLeaderboard();
        if (res.success && res.data && res.data.length > 0) {
          setTopStudents(res.data.map(s => ({
            id: s.id,
            name: [s.firstName, s.middleName, s.lastName].filter(Boolean).join(' '),
            points: s.points || 0,
            rank: s.grade === '1sec' ? 'أولى ثانوي' : s.grade === '2sec' ? 'تانية ثانوي' : 'تالتة ثانوي'
          })));
        } else {
          setTopStudents([]);
        }
      } catch {
        setTopStudents([]);
      }
    };
    fetchLeaderboard();

    // Show help message after 10 seconds
    const timer = setTimeout(() => setShowHelpMessage(true), 10000);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, []);

  const features = [
    {
      icon: CheckCircle,
      title: 'مرونة في المذاكرة',
      desc: 'ذاكر في أي وقت ومن أي مكان يناسبك'
    },
    {
      icon: Video,
      title: 'فيديوهات بشرح مبسط',
      desc: 'شرح وافي ومبسط لكل جزئيات المنهج'
    },
    {
      icon: Target,
      title: 'اختبارات مستمرة',
      desc: 'تقيم دوري لمستواك مع تصحيح فوري'
    },
    {
      icon: Headphones,
      title: 'دعم فني ومجتمع طلابي ضخم',
      desc: 'تواصل مع زملائك واستفسر في أي وقت'
    },
    {
      icon: BookOpen,
      title: 'التحديث المستمر',
      desc: 'مش من مصدر واحد - بنحل من كل المصادر'
    },
    {
      icon: Users,
      title: 'مجتمع متفاعل',
      desc: 'آلاف الطلاب في رحلة التفوق معاك'
    },
    {
      icon: Award,
      title: 'لوحة شرف',
      desc: 'تكريم أوائل الطلاب والمتميزين'
    },
    {
      icon: Clock,
      title: 'متابعة مستمرة',
      desc: 'تقارير دورية عن مستوى الطالب وأداءه'
    }
  ];

  // Dynamic theme classes
  const t = {
    bg: isDark ? 'bg-black' : 'bg-white',
    text: isDark ? 'text-white' : 'text-black',
    textMuted: isDark ? 'text-gray-400' : 'text-gray-600',
    textSubtle: isDark ? 'text-gray-500' : 'text-gray-500',
    glassBg: isDark ? 'rgba(0,0,0,0.65)' : 'rgba(255,255,255,0.65)',
    glassBorder: isDark ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.1)',
    btnPrimary: isDark
      ? 'bg-white text-black hover:bg-gray-200'
      : 'bg-black text-white hover:bg-gray-800',
    btnSecondary: isDark
      ? 'border-white/30 text-white hover:border-white/60 hover:bg-white/10'
      : 'border-black/30 text-black hover:border-black/60 hover:bg-black/10',
    cardHoverBorder: isDark ? 'hover:border-white/30' : 'hover:border-black/30',
    divider: isDark ? 'border-white/10' : 'border-black/10',
    scrollBarBg: isDark ? 'bg-white/20' : 'bg-black/20',
    scrollBarFill: isDark ? 'bg-white' : 'bg-black',
    overlay: isDark ? 'bg-black/50' : 'bg-white/30',
  };

  const bgImage = isDark ? '/images/desert-dark.png' : '/images/desert-light.png';

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-black' : 'bg-white'}`}>
        <div className="text-center">
          <div className={`w-16 h-16 border-4 ${isDark ? 'border-white' : 'border-black'} border-t-transparent rounded-full animate-spin mx-auto mb-4`}></div>
          <p className={isDark ? 'text-white' : 'text-black'}>جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen relative overflow-x-hidden transition-colors duration-700"
      style={{ 
        backgroundImage: `url('${bgImage}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Overlay for readability */}
      <div 
        className="fixed inset-0 pointer-events-none transition-colors duration-700 z-0"
        style={{ 
          background: isDark 
            ? 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0.85) 100%)'
            : 'linear-gradient(180deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0.7) 100%)'
        }}
      />

      {/* ═══════ FIXED NAVBAR ═══════ */}
      <header 
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
        style={{
          background: t.glassBg,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: 'none',
          borderBottom: t.glassBorder,
        }}
      >
        {/* Scroll progress bar */}
        <div className={`absolute bottom-0 left-0 right-0 h-[2px] ${t.scrollBarBg}`}>
          <div 
            className={`h-full ${t.scrollBarFill} transition-all duration-150`}
            style={{ width: `${scrollProgress}%` }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${isDark ? 'bg-white' : 'bg-black'} shadow-lg`}>
              <span className={`text-xl font-black ${isDark ? 'text-black' : 'text-white'}`}>♞</span>
            </div>
            <h1 className={`text-xl md:text-2xl font-black ${t.text}`}>
              سبيل <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>التفوق</span>
            </h1>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            {/* Day/Night Toggle */}
            <button
              onClick={() => setIsDark(!isDark)}
              className={`p-2.5 rounded-full transition-all duration-300 ${isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-black/10 hover:bg-black/20 text-black'}`}
              aria-label="Toggle theme"
              id="theme-toggle"
            >
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            {user ? (
              <button
                onClick={() => window.location.href = user?.role === 'admin' ? '/admin' : '/student'}
                className={`${t.btnPrimary} px-6 py-2 rounded-full font-bold transition-all duration-300 flex items-center gap-2 shadow-lg`}
              >
                <ArrowLeft size={18} />
                حسابي
              </button>
            ) : (
              <>
                <button
                  onClick={() => setShowLoginModal(true)}
                  className={`border-2 ${t.btnSecondary} px-4 md:px-6 py-2 rounded-full font-bold flex items-center gap-2 transition-all duration-300 backdrop-blur-sm text-sm md:text-base`}
                >
                  <LogIn size={16} />
                  تسجيل الدخول
                </button>
                <button
                  onClick={() => navigate('/login?tab=register')}
                  className={`${t.btnPrimary} px-4 md:px-6 py-2 rounded-full font-bold flex items-center gap-2 shadow-lg transition-all duration-300 hover:scale-105 text-sm md:text-base`}
                >
                  <UserPlus size={16} />
                  إنشاء حساب
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ═══════ HERO SECTION ═══════ */}
      <section className="relative z-10 container mx-auto px-4 pt-32 md:pt-40 pb-16 flex flex-col-reverse md:flex-row items-center gap-8">
        {/* Teacher Image — LEFT side on desktop */}
        <div className="flex-1 relative flex justify-center items-end mt-10 md:mt-0 order-2 md:order-1">
          <img
            src="/images/1.png"
            alt="أستاذ محمد حامد"
            className="relative z-10 w-full max-w-md md:max-w-xl lg:max-w-2xl mx-auto object-contain drop-shadow-[0_25px_50px_rgba(0,0,0,0.5)]"
            style={{ filter: 'grayscale(15%)' }}
          />
        </div>

        {/* Hero Text — RIGHT side on desktop */}
        <div className="flex-1 text-center md:text-right space-y-6 relative order-1 md:order-2">
          {/* Glass hero text box */}
          <div
            className="rounded-2xl p-6 md:p-8 transition-all duration-500"
            style={{
              background: t.glassBg,
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              border: t.glassBorder,
              boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(0,0,0,0.08)',
            }}
          >
            <div className={`inline-block px-4 py-2 rounded-full text-sm font-bold mb-4 ${isDark ? 'bg-white/10 text-white border border-white/20' : 'bg-black/10 text-black border border-black/20'}`}>
              🎯 أقوى منصة لغة عربية في مصر
            </div>

            <h1 className={`text-4xl md:text-6xl font-black leading-tight ${t.text} mb-4`}>
              اللغة العربية<br />
              <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                سبيل التفوق!
              </span>
            </h1>

            <p className={`text-lg md:text-xl max-w-lg mx-auto md:mx-0 ${t.textMuted} mb-6`}>
              مع أستاذ <span className={`font-bold ${t.text}`}>محمد حامد</span>.. النحو والبلاغة والأدب بقوا أسهل مما تتخيل.
            </p>

            <div className="flex flex-wrap gap-4 justify-center md:justify-start">
              <button
                onClick={() => setShowLoginModal(true)}
                className={`${t.btnPrimary} px-8 py-4 rounded-xl text-lg font-bold flex items-center gap-2 group shadow-2xl transition-all duration-300 hover:scale-105`}
              >
                ابدأ رحلة التفوق
                <ArrowLeft size={20} className="group-hover:translate-x-1 transition" />
              </button>
              
              <a
                href="#courses"
                className={`border-2 ${t.btnSecondary} px-8 py-4 rounded-xl text-lg font-bold flex items-center gap-2 transition-all duration-300`}
              >
                <BookOpen size={20} />
                شوف الكورسات
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ 8 FEATURE CARDS ═══════ */}
      <section className="relative z-10 py-20">
        <div 
          className="absolute inset-0 transition-all duration-500"
          style={{ 
            background: isDark ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.4)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
          }}
        />

        <div className="container mx-auto px-4 relative z-10">
          <h2 className={`text-3xl md:text-4xl font-black text-center mb-4 ${t.text}`}>
            ليه تختار <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>سبيل التفوق</span>؟
          </h2>
          <p className={`text-center mb-12 max-w-2xl mx-auto ${t.textMuted}`}>
            منصتنا مش مجرد شرح، دي تجربة متكاملة عشان الطالب يحقق أعلى الدرجات
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className={`group cursor-pointer rounded-xl p-5 md:p-6 transition-all duration-300 hover:scale-105 ${t.cardHoverBorder}`}
                style={{
                  background: t.glassBg,
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border: t.glassBorder,
                  boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.3)' : '0 4px 24px rgba(0,0,0,0.06)',
                }}
              >
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:rotate-6 transition-transform duration-300 ${isDark ? 'bg-white/10' : 'bg-black/10'}`}>
                  <feature.icon className={`w-7 h-7 ${t.text}`} />
                </div>
                <h3 className={`text-base md:text-lg font-bold mb-2 ${t.text}`}>
                  {feature.title}
                </h3>
                <p className={`text-sm ${t.textMuted}`}>
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ COURSES SECTION ═══════ */}
      <section id="courses" className="relative z-10 container mx-auto px-4 py-20">
        <h2 className={`text-3xl md:text-4xl font-black text-center mb-4 ${t.text} drop-shadow-lg`}>
          الكورسات <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>المتاحة</span>
        </h2>
        <p className={`text-center mb-12 max-w-2xl mx-auto ${t.textMuted}`}>
          اختر الباقة المناسبة لمستواك وابدأ رحلة التميز
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 justify-center max-w-6xl mx-auto">
          {courses.filter(course => course.status !== 'hidden').map((course) => {
            const isLocked = course.status === 'locked';
            return (
            <div
              key={course.id}
              className={`overflow-hidden group transition-all duration-300 rounded-xl ${isLocked ? 'opacity-75 cursor-not-allowed' : 'hover:scale-105 cursor-pointer'}`}
              style={{
                background: t.glassBg,
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: t.glassBorder,
                boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.5)' : '0 10px 30px rgba(0,0,0,0.08)',
              }}
              onClick={() => {
                if (isLocked) return;
                if (!user) {
                  setShowLoginModal(true);
                }
              }}
            >
              <div className={`h-48 relative overflow-hidden ${isDark ? 'bg-gray-900' : 'bg-gray-200'}`}>
                {course.image_url ? (
                  <img src={course.image_url} alt={course.title} className={`w-full h-full object-cover opacity-70 ${!isLocked ? 'group-hover:scale-110' : ''} transition duration-500 grayscale`} />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <BookOpen size={48} className={`opacity-30 ${t.text} ${!isLocked ? 'group-hover:scale-110' : ''} transition duration-500`} />
                  </div>
                )}
                <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-t from-black/90 via-black/40 to-transparent' : 'bg-gradient-to-t from-white/90 via-white/40 to-transparent'}`}></div>
                {isLocked && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <div className={`backdrop-blur-sm px-4 py-2 rounded-xl flex items-center gap-2 font-bold text-sm ${isDark ? 'bg-black/70 border border-white/20 text-white' : 'bg-white/70 border border-black/20 text-black'}`}>
                      🔒 الحجز مغلق
                    </div>
                  </div>
                )}
                <span className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold backdrop-blur-sm ${isDark ? 'bg-white/20 text-white' : 'bg-black/20 text-black'}`}>
                  {course.grade === '1sec' ? 'أولى ثانوي' : course.grade === '2sec' ? 'تانية ثانوي' : 'تالتة ثانوي'}
                </span>

                <div className={`absolute bottom-4 left-4 px-4 py-2 rounded-xl font-black shadow-lg ${isDark ? 'bg-white text-black' : 'bg-black text-white'}`}>
                  {course.price} <span className="text-xs">ج.م</span>
                </div>
              </div>

              <div className="p-6">
                <h3 className={`text-xl font-bold mb-2 ${t.text}`}>
                  {course.title}
                </h3>
                <p className={`text-sm mb-4 line-clamp-2 ${t.textMuted}`}>
                  {course.description}
                </p>
                <div className={`flex items-center justify-between border-t pt-4 mt-2 ${t.divider}`}>
                  <span className={`text-sm flex items-center gap-1 ${t.textSubtle}`}>
                    <Clock size={14} />
                    {course.duration_days || course.duration || 30} يوم
                  </span>
                  {isLocked ? (
                    <span className={`font-bold text-sm flex items-center gap-1 cursor-not-allowed ${t.textSubtle}`}>
                      🔒 الحجز مغلق
                    </span>
                  ) : (
                    <button className={`${t.text} hover:opacity-70 transition font-bold text-sm flex items-center gap-1`}>
                      اشترك الآن <ArrowLeft size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      </section>

      {/* ═══════ HONORED STUDENTS ═══════ */}
      <section className="relative z-10 py-20">
        <div 
          className="absolute inset-0 transition-all duration-500"
          style={{ 
            background: isDark ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.4)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
          }}
        />

        <div className="container mx-auto px-4 relative z-10">
          <h2 className={`text-3xl md:text-4xl font-black text-center mb-4 ${t.text}`}>
            ♞ فرسان <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>التفوق</span>
          </h2>
          <p className={`text-center mb-12 max-w-2xl mx-auto ${t.textMuted}`}>
            نخبة المتميزين في رحلتنا نحو التفوق
          </p>

          {topStudents.length === 0 ? (
            <div className="text-center py-12">
              <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center ${isDark ? 'bg-white/10' : 'bg-black/10'}`}>
                <Award size={40} className={`${t.textSubtle}`} />
              </div>
              <p className={`text-lg font-bold ${t.textMuted}`}>قريباً... سيتم تحديد لوحة الشرف من قبل الإدارة</p>
            </div>
          ) : (
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {topStudents.map((student) => (
              <div
                key={student.id}
                className="p-6 text-center rounded-xl hover:-translate-y-2 transition-transform relative overflow-hidden"
                style={{
                  background: t.glassBg,
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border: t.glassBorder,
                  boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.4)' : '0 10px 30px rgba(0,0,0,0.06)',
                }}
              >
                <div className={`absolute top-0 left-0 w-full h-1 ${isDark ? 'bg-white' : 'bg-black'}`}></div>
                <div className={`w-24 h-24 mx-auto mb-4 rounded-full flex items-center justify-center border-4 shadow-xl ${isDark ? 'bg-white/10 border-white/30' : 'bg-black/10 border-black/30'}`}>
                  <span className={`text-3xl ${t.text}`}>♞</span>
                </div>
                <h3 className={`text-xl font-bold mb-2 ${t.text}`}>{student.name}</h3>
                <div className={`inline-block px-3 py-1 rounded-full text-sm font-bold mb-3 ${isDark ? 'bg-white/10 text-gray-300 border border-white/20' : 'bg-black/10 text-gray-700 border border-black/20'}`}>
                  {student.rank}
                </div>
                <div className={`text-2xl font-black ${t.text} drop-shadow-md`}>{student.points}</div>
                <div className={`text-sm ${t.textSubtle}`}>نقطة</div>
              </div>
            ))}
          </div>
          )}
        </div>
      </section>

      {/* ═══════ CTA SECTION ═══════ */}
      <section className="relative z-10 container mx-auto px-4 py-20">
        <div 
          className="relative rounded-3xl overflow-hidden"
          style={{
            background: t.glassBg,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: t.glassBorder,
            boxShadow: isDark ? '0 20px 50px rgba(0,0,0,0.5)' : '0 20px 50px rgba(0,0,0,0.08)',
          }}
        >
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 relative h-64 md:h-[400px] w-full">
              <img
                src="/images/1.png"
                alt=""
                className="w-full h-full object-cover opacity-60"
                style={{ filter: 'grayscale(30%)' }}
              />
              <div 
                className="absolute inset-0" 
                style={{ 
                  background: isDark 
                    ? 'linear-gradient(to left, rgba(0,0,0,0.9), transparent)'
                    : 'linear-gradient(to left, rgba(255,255,255,0.9), transparent)' 
                }}
              />
            </div>

            <div className="md:w-1/2 p-8 md:p-12 text-center md:text-right relative z-10 -mt-10 md:mt-0">
              <h2 className={`text-3xl md:text-5xl font-black mb-4 ${t.text} drop-shadow-lg`}>
                مستني إيه؟ <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>يلا بينا</span>
              </h2>
              <p className={`text-lg md:text-xl mb-8 max-w-2xl mx-auto md:mx-0 ${t.textMuted} leading-relaxed`}>
                انضم لأكتر من 5000 طالب بدأوا رحلة التفوق في اللغة العربية
              </p>
              <button
                onClick={() => setShowLoginModal(true)}
                className={`${t.btnPrimary} px-10 py-4 rounded-xl text-xl font-bold inline-flex items-center justify-center gap-3 group shadow-2xl transition-all duration-300 hover:scale-105 w-full md:w-auto`}
              >
                <LogIn size={24} />
                سجل دخولك دلوقتي
                <ArrowLeft size={20} className="group-hover:translate-x-1 transition" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ FOOTER ═══════ */}
      <footer 
        className="relative z-10 mt-auto"
        style={{
          background: isDark ? 'rgba(0,0,0,0.85)' : 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderTop: t.glassBorder,
        }}
      >
        <div className="max-w-6xl mx-auto px-4 py-6">
          {/* Top Section: Logo/Name */}
          <div className="text-center mb-6">
            <h2 className={`text-3xl font-black font-cairo ${t.text}`}>الأستاذ محمد حامد</h2>
          </div>

          {/* Middle Section: Copyright & Developers */}
          <div className="text-center mb-8">
            <p className={`text-sm mb-4 font-cairo ${t.textMuted}`}>جميع الحقوق محفوظة © 2026</p>
            <div className="text-center" dir="ltr">
              <span className="text-red-500">&lt;</span>
              <span className="text-blue-500">Developed by</span>
              <span className="text-green-500">=</span>
              <span className="text-red-500">"</span>
              
              {/* Marwan Sami */}
              <span className="relative group inline-block">
                <span className="text-yellow-500 cursor-pointer">Marwan Sami (ابو ريان)</span>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:flex flex-col bg-slate-800 text-white text-xs rounded-lg p-2 shadow-lg z-50 w-32">
                  <a href="https://wa.me/201064756266" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:bg-slate-700 px-2 py-1 rounded transition-colors">
                    <MessageCircle size={12} /> WhatsApp
                  </a>
                  <a href="https://www.instagram.com/maro.og7/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:bg-slate-700 px-2 py-1 rounded transition-colors">
                    <Instagram size={12} /> Instagram
                  </a>
                </div>
              </span>
              
              <span className="text-red-500"> , </span>
              
              {/* Anas Hany */}
              <span className="relative group inline-block">
                <span className="text-yellow-500 cursor-pointer">Anas Hany (ابو ادام)</span>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:flex flex-col bg-slate-800 text-white text-xs rounded-lg p-2 shadow-lg z-50 w-32">
                  <a href="https://wa.me/201028608446" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:bg-slate-700 px-2 py-1 rounded transition-colors">
                    <MessageCircle size={12} /> WhatsApp
                  </a>
                  <a href="https://www.instagram.com/anas_aboadam555/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:bg-slate-700 px-2 py-1 rounded transition-colors">
                    <Instagram size={12} /> Instagram
                  </a>
                </div>
              </span>
              
              <span className="text-red-500">"</span>
              <span className="text-blue-500"> andPowered</span>
              <span className="text-green-500">=</span>
              <span className="text-orange-500">true</span>
              <span className="text-red-500"> /&gt;</span>
            </div>
          </div>

          {/* Bottom Section: Two Columns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-right">
            {/* Right Column: الصفحات */}
            <div>
              <h3 className={`text-xl font-bold mb-4 font-cairo ${t.text} text-red-500`}>الصفحات</h3>
              <ul className="space-y-2">
                <li><a href="/" className={`${t.textMuted} hover:${t.text} transition font-cairo`}>الرئيسية</a></li>
                <li><a href="https://wa.me/201028608446?text=السلام عليكم، لدي مشكلة في منصة سبيل التفوق وهي: " target="_blank" rel="noopener noreferrer" className={`${t.textMuted} hover:${t.text} transition font-cairo`}>المساعدة / الدعم الفني</a></li>
              </ul>
            </div>

            {/* Left Column: السوشيال ميديا */}
            <div>
              <h3 className={`text-xl font-bold mb-4 font-cairo ${t.text} text-red-500`}>السوشيال ميديا</h3>
              <div className="flex justify-end gap-4">
                <a href="https://youtube.com/channel/UCHJwRJSN1A1bOIVWholqQMg?si=RQHu2xXSgF2ReDRl" target="_blank" rel="noopener noreferrer" className={`p-3 rounded-xl transition-all duration-300 hover:-translate-y-1 ${isDark ? 'bg-white/5 text-white hover:bg-white/20' : 'bg-black/5 text-black hover:bg-black/20'}`}>
                  <Youtube size={24} />
                </a>
                <a href="https://www.facebook.com/share/1LQkyhgFRt/" target="_blank" rel="noopener noreferrer" className={`p-3 rounded-xl transition-all duration-300 hover:-translate-y-1 ${isDark ? 'bg-white/5 text-white hover:bg-white/20' : 'bg-black/5 text-black hover:bg-black/20'}`}>
                  <Facebook size={24} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* ═══════ LOGIN MODAL ═══════ */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[200] flex items-center justify-center p-4" onClick={() => setShowLoginModal(false)}>
          <div 
            className="p-8 max-w-md w-full animate-slide-up rounded-2xl relative overflow-hidden"
            style={{
              background: isDark ? 'rgba(0,0,0,0.9)' : 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              border: isDark ? '2px solid rgba(255,255,255,0.2)' : '2px solid rgba(0,0,0,0.2)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.8)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className={`absolute top-0 left-0 right-0 h-1 ${isDark ? 'bg-white' : 'bg-black'}`}></div>

            <button onClick={() => setShowLoginModal(false)} className={`absolute top-4 left-4 p-2 rounded-full transition ${isDark ? 'hover:bg-white/20' : 'hover:bg-black/20'}`}>
              <X size={20} className={t.text} />
            </button>

            <div className="text-center mb-8 mt-4">
              <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 border-4 shadow-xl ${isDark ? 'bg-white border-white/30' : 'bg-black border-black/30'}`}>
                <span className={`text-4xl ${isDark ? 'text-black' : 'text-white'}`}>♞</span>
              </div>
              <h2 className={`text-2xl font-black mb-2 ${t.text}`}>أهلاً بك في سبيل التفوق</h2>
              <p className={t.textMuted}>سجل دخولك وابدأ رحلة التعلم</p>
            </div>

            <div className="space-y-4">
              <button
                onClick={() => {
                  setShowLoginModal(false);
                  navigate('/login');
                }}
                className={`w-full ${t.btnPrimary} p-4 rounded-xl font-bold flex items-center justify-center gap-3 transition shadow-lg hover:scale-[1.02]`}
              >
                <LogIn size={20} />
                الانتقال لتسجيل الدخول
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3 py-1">
                <div className={`flex-1 h-px ${isDark ? 'bg-white/20' : 'bg-black/20'}`}></div>
                <span className={`text-xs font-bold ${t.textSubtle}`}>أو</span>
                <div className={`flex-1 h-px ${isDark ? 'bg-white/20' : 'bg-black/20'}`}></div>
              </div>

              {/* Registration CTA */}
              <div className="text-center space-y-3">
                <p className={`text-sm ${t.textMuted}`}>ليس لديك حساب؟</p>
                <button
                  onClick={() => {
                    setShowLoginModal(false);
                    navigate('/login?tab=register');
                  }}
                  className={`w-full border-2 ${t.btnSecondary} p-4 rounded-xl font-bold flex items-center justify-center gap-3 transition-all hover:scale-[1.02]`}
                >
                  <UserPlus size={20} />
                  إنشاء حساب جديد
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════ HELP MESSAGE ═══════ */}
      {showHelpMessage && !user && (
        <div className="fixed bottom-8 left-8 z-50">
          {showHelpMenu && (
            <div className="absolute bottom-full mb-4 left-0 w-64 rounded-3xl bg-white/10 backdrop-blur-xl border border-white/20 shadow-2xl p-3 flex flex-col gap-3">
              <a
                href="https://wa.me/201028608446"
                target="_blank"
                rel="noreferrer noopener"
                className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-black/80 text-white hover:bg-emerald-500 transition-colors"
              >
                <MessageCircle size={18} />
                الدعم الفني (ابو ادام)
              </a>
              <a
                href="https://wa.me/201064756266"
                target="_blank"
                rel="noreferrer noopener"
                className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-black/80 text-white hover:bg-emerald-500 transition-colors"
              >
                <MessageCircle size={18} />
                مروان سامي
              </a>
            </div>
          )}
          <div
            onClick={() => setShowHelpMenu(!showHelpMenu)}
            className="cursor-pointer animate-slide-up hover:-translate-y-1 transition-all flex items-center gap-4 p-4 rounded-2xl"
            style={{
              background: isDark ? 'rgba(0,0,0,0.9)' : 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              border: t.glassBorder,
              boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.5)' : '0 10px 30px rgba(0,0,0,0.1)',
            }}
          >
            <div className={`w-12 h-12 rounded-full flex items-center justify-center animate-pulse shadow-inner ${isDark ? 'bg-white' : 'bg-black'}`}>
              <span className={`text-2xl ${isDark ? 'text-black' : 'text-white'}`}>♞</span>
            </div>
            <div>
              <h4 className={`font-bold mb-1 ${t.text}`}>محتاج مساعدة؟</h4>
              <p className={`text-xs ${t.textMuted}`}>اضغط هنا للبدء وتخطي الصعاب</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};